import { useEffect, type ReactElement } from 'react';
import {
    Alert,
    Badge,
    Box,
    Button,
    Center,
    Checkbox,
    Flex,
    Group,
    Loader,
    Modal,
    ScrollArea,
    Stack,
    Tabs,
    Text,
    TextInput,
    Textarea,
    UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { KeyRound, Lock, Plus, Search, ShieldCheck, Trash2 } from 'lucide-react';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import { SurfaceCard } from '@/design/SurfaceCard';
import { RolesTabViewModel } from './RolesTabViewModel';
import { PermissionsTabViewModel } from './PermissionsTabViewModel';
import type { RoleView } from './RolesAdminService';

export function RolesPermissionsPage(): ReactElement {
    return (
        <Box>
            <PageHeaderComponent
                title="Roles & Permissions"
                subtitle="Define custom roles, assign what they can access, and review the permission catalog."
            />
            <Tabs defaultValue="roles">
                <Tabs.List mb="xl">
                    <Tabs.Tab value="roles" leftSection={<ShieldCheck size={14} />}>
                        Roles
                    </Tabs.Tab>
                    <Tabs.Tab value="permissions" leftSection={<KeyRound size={14} />}>
                        Permission catalog
                    </Tabs.Tab>
                </Tabs.List>
                <Tabs.Panel value="roles">
                    <RolesTab />
                </Tabs.Panel>
                <Tabs.Panel value="permissions">
                    <PermissionsTab />
                </Tabs.Panel>
            </Tabs>
        </Box>
    );
}

/** Total granted (resource, action) pairs for a role. */
function permissionCount(role: RoleView): number {
    return role.permissions.reduce((n, g) => n + g.scopes.length, 0);
}

function RolesTab(): ReactElement {
    const vm = useViewModel(RolesTabViewModel);

    useEffect(() => {
        void vm.load();
    }, [vm]);

    async function handleCreate(): Promise<void> {
        const result = await vm.createRole();
        if (result.success) {
            notifications.show({ title: 'Role created', message: result.name, color: 'green' });
        } else if (result.error) {
            notifications.show({ title: 'Error', message: result.error, color: 'red' });
        }
    }

    async function handleSavePermissions(): Promise<void> {
        const result = await vm.savePermissions();
        if (result.success) {
            notifications.show({ title: 'Permissions saved', message: vm.selectedRole?.name ?? '', color: 'green' });
        } else if (result.error) {
            notifications.show({ title: 'Error', message: result.error, color: 'red' });
        }
    }

    async function handleDelete(): Promise<void> {
        const result = await vm.confirmDelete();
        if (result.success) {
            notifications.show({ title: 'Role deleted', message: result.name, color: 'green' });
        } else if (result.error) {
            notifications.show({ title: 'Error', message: result.error, color: 'red' });
        }
    }

    if (vm.loading && vm.roles === null) {
        return (
            <Center py="xl">
                <Loader />
            </Center>
        );
    }

    const roles = vm.roles ?? [];

    return (
        <>
            <Flex gap="lg" align="stretch" direction={{ base: 'column', md: 'row' }}>
                <SurfaceCard $padding="md" $static style={{ flex: '0 0 260px', alignSelf: 'stretch' }}>
                    <Group justify="space-between" mb="sm">
                        <Text size="sm" fw={600}>
                            Roles
                        </Text>
                        <Button size="xs" variant="light" leftSection={<Plus size={14} />} onClick={() => vm.openCreate()}>
                            New
                        </Button>
                    </Group>
                    <Stack gap={2}>
                        {roles.map((role) => {
                            const selected = role.id === vm.selectedRoleId;
                            return (
                                <UnstyledButton
                                    key={role.id}
                                    onClick={() => vm.selectRole(role.id)}
                                    style={{
                                        padding: '8px 10px',
                                        borderRadius: 'var(--bm-radius)',
                                        background: selected ? 'var(--mantine-primary-color-light)' : undefined,
                                    }}
                                >
                                    <Group gap={6} wrap="nowrap" justify="space-between">
                                        <Box style={{ minWidth: 0 }}>
                                            <Group gap={6} wrap="nowrap">
                                                <Text size="sm" fw={selected ? 600 : 500} truncate>
                                                    {role.name}
                                                </Text>
                                                {role.isSystem && <Lock size={11} color="var(--bm-color-text-dimmed)" />}
                                            </Group>
                                            <Text size="xs" c="dimmed">
                                                {permissionCount(role)} permission{permissionCount(role) === 1 ? '' : 's'}
                                            </Text>
                                        </Box>
                                    </Group>
                                </UnstyledButton>
                            );
                        })}
                        {roles.length === 0 && (
                            <Text size="sm" c="dimmed" ta="center" py="md">
                                No roles yet.
                            </Text>
                        )}
                    </Stack>
                </SurfaceCard>

                <SurfaceCard $padding="lg" $static style={{ flex: 1, minWidth: 0 }}>
                    {vm.selectedRole ? (
                        <RoleDetail vm={vm} role={vm.selectedRole} onSave={handleSavePermissions} />
                    ) : (
                        <Center py="xl" style={{ height: '100%' }}>
                            <Stack align="center" gap="xs">
                                <ShieldCheck size={28} color="var(--bm-color-text-dimmed)" />
                                <Text size="sm" c="dimmed">
                                    Select a role to view its permissions.
                                </Text>
                            </Stack>
                        </Center>
                    )}
                </SurfaceCard>
            </Flex>

            <Modal opened={vm.modalOpen} onClose={() => vm.closeModal()} title="New role" centered>
                <Stack>
                    <TextInput
                        label="Role name"
                        placeholder="executive"
                        required
                        value={vm.roleName}
                        onChange={(e) => vm.setRoleName(e.currentTarget.value)}
                        description="Lowercase identifier — can't be changed after creation."
                    />
                    <Textarea
                        label="Description"
                        placeholder="What this role is for…"
                        value={vm.roleDesc}
                        onChange={(e) => vm.setRoleDesc(e.currentTarget.value)}
                        autosize
                        minRows={2}
                    />
                    <Text size="xs" c="dimmed">
                        You'll choose this role's permissions after creating it.
                    </Text>
                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => vm.closeModal()}>
                            Cancel
                        </Button>
                        <Button onClick={handleCreate} loading={vm.saving}>
                            Create role
                        </Button>
                    </Group>
                </Stack>
            </Modal>

            <Modal opened={vm.deleteTarget !== null} onClose={() => vm.closeDelete()} title="Delete role" centered size="sm">
                <Stack>
                    <Text size="sm">
                        Delete role <strong>{vm.deleteTarget?.name}</strong>? Users who have only this role will lose its
                        access. This can't be undone.
                    </Text>
                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => vm.closeDelete()}>
                            Cancel
                        </Button>
                        <Button color="red" loading={vm.saving} onClick={handleDelete}>
                            Delete role
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </>
    );
}

function RoleDetail({
    vm,
    role,
    onSave,
}: {
    vm: RolesTabViewModel;
    role: RoleView;
    onSave: () => void;
}): ReactElement {
    const groups = vm.filteredCatalogGroups;
    return (
        <Stack gap="lg">
            <Group justify="space-between" align="flex-start" wrap="nowrap">
                <Box style={{ minWidth: 0 }}>
                    <Group gap="sm">
                        <Text size="lg" fw={700}>
                            {role.name}
                        </Text>
                        {role.isSystem ? (
                            <Badge size="sm" variant="light" color="gray" leftSection={<Lock size={10} />}>
                                System
                            </Badge>
                        ) : (
                            <Badge size="sm" variant="light" color="blue">
                                Custom
                            </Badge>
                        )}
                    </Group>
                    {role.description && (
                        <Text size="sm" c="dimmed" mt={4}>
                            {role.description}
                        </Text>
                    )}
                </Box>
                {!role.isSystem && (
                    <Button
                        size="xs"
                        variant="light"
                        color="red"
                        leftSection={<Trash2 size={14} />}
                        onClick={() => vm.openDelete(role)}
                        style={{ flexShrink: 0 }}
                    >
                        Delete
                    </Button>
                )}
            </Group>

            {role.isSystem && (
                <Alert variant="light" color="gray" icon={<Lock size={16} />}>
                    System roles are managed by the platform — their permissions can't be edited.
                </Alert>
            )}

            <Box>
                <Group justify="space-between" mb="sm">
                    <Text size="sm" fw={600}>
                        Permissions
                    </Text>
                    <Text size="xs" c="dimmed">
                        {vm.grantedCount} granted
                    </Text>
                </Group>
                <TextInput
                    size="xs"
                    placeholder="Filter resources or actions"
                    leftSection={<Search size={14} />}
                    value={vm.permissionSearch}
                    onChange={(e) => vm.setPermissionSearch(e.currentTarget.value)}
                    mb="sm"
                />
                <ScrollArea.Autosize mah={440} type="auto">
                    <Stack gap="lg">
                        {groups.map((grp) => (
                            <Box key={grp.group}>
                                <Text
                                    size="xs"
                                    fw={700}
                                    tt="uppercase"
                                    c="dimmed"
                                    mb="xs"
                                    style={{ letterSpacing: '0.05em' }}
                                >
                                    {grp.label}
                                </Text>
                                <Stack gap={0}>
                                    {grp.resources.map((meta, i) => (
                                        <Box
                                            key={meta.resource}
                                            py="sm"
                                            style={{
                                                borderTop:
                                                    i > 0 ? '1px solid var(--bm-color-border-subtle)' : undefined,
                                            }}
                                        >
                                            {role.isSystem ? (
                                                <Group justify="space-between" wrap="nowrap" align="flex-start">
                                                    <Box style={{ minWidth: 0 }}>
                                                        <Text size="sm" fw={500}>
                                                            {meta.label}
                                                        </Text>
                                                        <Text size="xs" c="dimmed">
                                                            {meta.description}
                                                        </Text>
                                                    </Box>
                                                    <Group gap={6} justify="flex-end" style={{ flex: 1 }}>
                                                        {meta.actions.filter((a) => vm.isScopeGranted(meta.resource, a))
                                                            .length === 0 ? (
                                                            <Text size="xs" c="dimmed">
                                                                —
                                                            </Text>
                                                        ) : (
                                                            meta.actions
                                                                .filter((a) => vm.isScopeGranted(meta.resource, a))
                                                                .map((a) => (
                                                                    <Badge key={a} size="xs" variant="light">
                                                                        {a}
                                                                    </Badge>
                                                                ))
                                                        )}
                                                    </Group>
                                                </Group>
                                            ) : (
                                                <>
                                                    <Checkbox
                                                        size="sm"
                                                        checked={vm.isResourceFullyGranted(meta.resource)}
                                                        indeterminate={vm.isResourcePartiallyGranted(meta.resource)}
                                                        onChange={() => vm.toggleResourceAll(meta.resource)}
                                                        label={
                                                            <Text size="sm" fw={600}>
                                                                {meta.label}
                                                            </Text>
                                                        }
                                                    />
                                                    <Group gap="lg" pl={30} mt={6}>
                                                        {meta.actions.map((a) => (
                                                            <Checkbox
                                                                key={a}
                                                                size="xs"
                                                                label={a}
                                                                checked={vm.isScopeGranted(meta.resource, a)}
                                                                onChange={() => vm.toggleScope(meta.resource, a)}
                                                            />
                                                        ))}
                                                    </Group>
                                                </>
                                            )}
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        ))}
                        {groups.length === 0 && (
                            <Text size="sm" c="dimmed" py="md">
                                No matching resources.
                            </Text>
                        )}
                    </Stack>
                </ScrollArea.Autosize>
            </Box>

            {!role.isSystem && (
                <Group>
                    <Button onClick={onSave} loading={vm.saving} disabled={!vm.dirty}>
                        Save permissions
                    </Button>
                    {vm.dirty && (
                        <Button variant="subtle" color="gray" onClick={() => vm.resetDraft()}>
                            Reset
                        </Button>
                    )}
                    {vm.dirty && (
                        <Text size="xs" c="dimmed">
                            Unsaved changes
                        </Text>
                    )}
                </Group>
            )}
        </Stack>
    );
}

function PermissionsTab(): ReactElement {
    const vm = useViewModel(PermissionsTabViewModel);

    useEffect(() => {
        void vm.load();
    }, [vm]);

    if (vm.loading) {
        return (
            <Center py="xl">
                <Loader />
            </Center>
        );
    }

    const groups = vm.filteredGroups;

    return (
        <SurfaceCard $padding="lg" $static>
            <Stack gap="md">
                <Box>
                    <Text size="sm" fw={600}>
                        Permission catalog
                    </Text>
                    <Text size="xs" c="dimmed">
                        Registered by domain services — {vm.totalPermissions} permission
                        {vm.totalPermissions === 1 ? '' : 's'} across {vm.resourceGroups.length} resource
                        {vm.resourceGroups.length === 1 ? '' : 's'}. Assign them to custom roles on the Roles tab.
                    </Text>
                </Box>
                <TextInput
                    size="xs"
                    placeholder="Filter resources or actions"
                    leftSection={<Search size={14} />}
                    value={vm.search}
                    onChange={(e) => vm.setSearch(e.currentTarget.value)}
                />
                <Stack gap={0}>
                    {groups.map((g, i) => (
                        <Group
                            key={g.resource}
                            justify="space-between"
                            wrap="nowrap"
                            align="flex-start"
                            py="sm"
                            style={{ borderTop: i > 0 ? '1px solid var(--bm-color-border-subtle)' : undefined }}
                        >
                            <Text size="sm" fw={500}>
                                {g.resource}
                            </Text>
                            <Group gap={6} justify="flex-end" style={{ flex: 1 }}>
                                {g.actions.map((action) => (
                                    <Badge key={action} size="sm" variant="light">
                                        {action}
                                    </Badge>
                                ))}
                            </Group>
                        </Group>
                    ))}
                    {groups.length === 0 && (
                        <Text size="sm" c="dimmed" py="md">
                            No matching permissions.
                        </Text>
                    )}
                </Stack>
            </Stack>
        </SurfaceCard>
    );
}
